import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    IconButton,
    Snackbar,
    Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const TaskModal = ({ task, parents = [], customers = [], open, onClose, onSave, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [taskDetails, setTaskDetails] = useState(task);
    const [feedback, setFeedback] = useState({ message: "", severity: "", open: false });

    const handleSave = async () => {
        try {
            await onSave(taskDetails);
            setFeedback({ message: "Task updated successfully!", severity: "success", open: true });
            setIsEditing(false);
        } catch (error) {
            setFeedback({ message: "Failed to update task. Try again.", severity: "error", open: true });
        }
    };

    const handleDelete = async () => {
        try {
            await onDelete(task.id);
            onClose(); // Close the modal after deleting
        } catch (error) {
            setFeedback({ message: "Failed to delete task. Try again.", severity: "error", open: true });
        }
    };

    const handleCloseSnackbar = () => setFeedback({ ...feedback, open: false });

    return (
        <>
            <Snackbar
                open={feedback.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={feedback.severity} sx={{ width: "100%" }}>
                    {feedback.message}
                </Alert>
            </Snackbar>

            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    {isEditing ? "Edit Task" : "Task Details"}
                    {!isEditing && (
                        <IconButton
                            onClick={() => setIsEditing(true)}
                            sx={{ float: "right" }}
                            aria-label="edit"
                        >
                            <EditIcon />
                        </IconButton>
                    )}
                </DialogTitle>

                <DialogContent>
                    {isEditing ? (
                        <>
                            <TextField
                                label="Title"
                                fullWidth
                                margin="normal"
                                value={taskDetails.title}
                                onChange={(e) => setTaskDetails({ ...taskDetails, title: e.target.value })}
                            />
                            <TextField
                                label="Description"
                                fullWidth
                                margin="normal"
                                multiline
                                rows={3}
                                value={taskDetails.description}
                                onChange={(e) =>
                                    setTaskDetails({ ...taskDetails, description: e.target.value })
                                }
                            />
                            <TextField
                                label="Priority"
                                fullWidth
                                margin="normal"
                                select
                                value={taskDetails.priority}
                                onChange={(e) => setTaskDetails({ ...taskDetails, priority: e.target.value })}
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                                <option value="Immediate">Immediate</option>
                            </TextField>
                            <TextField
                                label="Parent"
                                fullWidth
                                margin="normal"
                                select
                                value={taskDetails.parent}
                                onChange={(e) => setTaskDetails({ ...taskDetails, parent: e.target.value })}
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                {parents.map((parent) => (
                                    <option key={parent} value={parent}>
                                        {parent}
                                    </option>
                                ))}
                            </TextField>
                            <TextField
                                label="Starting Date"
                                fullWidth
                                margin="normal"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={taskDetails.startDate}
                                onChange={(e) =>
                                    setTaskDetails({ ...taskDetails, startDate: e.target.value })
                                }
                            />
                            <TextField
                                label="Deadline Date"
                                fullWidth
                                margin="normal"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={taskDetails.deadlineDate}
                                onChange={(e) =>
                                    setTaskDetails({ ...taskDetails, deadlineDate: e.target.value })
                                }
                            />
                            <TextField
                                label="Customer"
                                fullWidth
                                margin="normal"
                                select
                                value={taskDetails.customerId || ""}
                                onChange={(e) =>
                                    setTaskDetails({ ...taskDetails, customerId: e.target.value })
                                }
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                <option value="">None</option>
                                {customers.map((customer) => (
                                    <option key={customer.id} value={customer.id}>
                                        {customer.name}
                                    </option>
                                ))}
                            </TextField>
                        </>
                    ) : (
                        <>
                            <Typography variant="h6">{task.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {task.description || "No description provided."}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Priority: {task.priority}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Parent: {task.parent || "General"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Start: {task.startDate || "N/A"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Deadline: {task.deadlineDate || "N/A"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Customer:{" "}
                                {customers.find((c) => c.id === task.customerId)?.name || "None"}
                            </Typography>
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleDelete}
                        variant="outlined"
                        color="secondary"
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </Button>
                    {isEditing ? (
                        <Button onClick={handleSave} variant="contained" color="primary">
                            Save
                        </Button>
                    ) : (
                        <Button onClick={onClose}>Close</Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TaskModal;
